//API or "domain" types for orders
import {
  OrderRowTemplateSalaryLimit,
  SupplementPriceOverridesType,
  SupplementsType,
  VariationSelection,
} from "../../common/types/apiTypes";
import { AutocompleteType } from "../../common/types/autoComplete";
import { Nullable } from "../../common/types/nullable";
import { InvoiceReferences } from "../customer/types/commands";
import { CustomerCC, Identifier, RiskLevel } from "../customer/types/queries";
import { TargetingRule } from "../employment/employmentApi";
import { LanguageSkillCode } from "../talents/talentAPI";
import {
  FulfillmentStatus,
  OrderEdit,
  OrderRowEdit,
  OrderType,
} from "./orderAPI";
import { TalentPool } from "./types/common";

export const autoCompleteDefaultValue = { id: "", name: "" };
//Wrong name, name should be OrderRowTemplate
// TODO: could this use the same type from the common types
export type OrderTemplate = {
  id: string;
  name: string;
  contractId: string;
  tesId: string;
  tes: AutocompleteType;
  tesVariation: VariationSelection[];
  salaryLimit: string | null;
  salaryLimits: OrderRowTemplateSalaryLimit[];
  skills: AutocompleteType[];
  certificates: AutocompleteType[];
  languages: LanguageSkill[];
  trainings: AutocompleteType[];
  workTitleId: string;
  supplements: SupplementsType; //supplement key: mode
  supplementPriceOverrides: SupplementPriceOverridesType; // supplement key: price
  periodStartDate: Date | null;
  isBillable: boolean;
};

export type Address = {
  street: string;
  zip: string;
  city: string;
  country?: string;
};

export type Info = {
  fi: string;
  en: string;
};

export type WorkLocation = {
  id: string;
  customerOrgId: string;
  contractId: string;
  name: string;
  address: Address;
  customerCCs: CustomerCC[];
  info: {
    [key: string]: Info;
  };
  descriptionForTalent: string;
  descriptionForTalentEng: string;
  version: number; // TODO: not needed?
  type: string;
  active: boolean;
  talentCustomerContactPersons: Identifier[];
  invoiceContactPerson: Nullable<Identifier>;
  invoiceReferences: Nullable<InvoiceReferences>;
};

export enum AutomatchingStatus {
  On = "On",
  Off = "Off",
  Paused = "Paused",
}

export interface Automatching {
  talents: AutocompleteType[];
  automatchingStatus: AutomatchingStatus;
  automatchingStartDelayMinutes: number | null;
  talentsFromWorkLocationPool?: boolean;
  pool: Nullable<TalentPool>;
}

export type HandleChangeValue =
  | string
  | number
  | Date
  | null
  | AutocompleteType
  | AutocompleteType[]
  | string[]
  | Automatching
  | boolean;

export interface OrderFields {
  id: string;
  workLocation: WorkLocation;
  // REVIEW: why is orderTemplate here?
  orderTemplate: OrderTemplate;
  amount: number;
  date: Date | null;
  time: string;
  customerCostCenter: AutocompleteType;
  reasonForTemporaryEmployment: string;
  createdAt: EpochTimeStamp;
  descriptionToEezyAndCustomer: string;
  descriptionToEezyAndCustomerAndTalent?: string;
  title?: string;
  orderer?: string;
  customerOrgName?: string;
  version?: string;
  description?: string;
  descriptionEn?: string;
  isFillShift: boolean | null;
  automatching: Automatching;
  flexibleStartUntil?: string;
  notifyWhenFulfilled?: boolean;
  notifyMinutesBeforeIfNotFulfilled?: number;
}

export type LongOrderFields = Omit<
  OrderFields,
  "title" | "orderer" | "customerOrgName" | "version" | "offerManually"
>;

// REVIEW: OrderRow interface has property rows that contains OrderFields. What is going on here?
// REVIEW: should bundleId change the data format?
export interface OrderRow {
  rowId: string;
  createdAt: EpochTimeStamp; //TODO: remove this at one point
  bundleId?: string;
  rows: OrderFields[];
}

export interface FixedTermDetails {
  startDate: Date | null;
  endDate: Date | null;
  shortWorkingHoursFi: string;
  longWorkingHoursFi: string;
  shortWorkingHoursEn: string;
  longWorkingHoursEn: string;
}

export interface LongOrderRow {
  rowId: string;
  bundleId?: string;
  createdAt: EpochTimeStamp; //TODO remove this at one point
  id: string; //This is basically only for BundeleId handling, remove!!
  workLocation: WorkLocation;
  workTitleId: string;
  workTitleName: string;
  tes: AutocompleteType;
  tesVariation: VariationSelection[];
  salaryLimit: null;
  skills: AutocompleteType[];
  certificates: AutocompleteType[];
  languages: LanguageSkill[];
  trainings: AutocompleteType[];
  amount: number;
  date: Date | null;
  time: string;
  customerCostCenters: AutocompleteType[];
  reasonForTemporaryEmployment: string;
  descriptionToEezyAndCustomer: string;
  fixedTermDetails: FixedTermDetails;
  automatching: Automatching;
}

export interface FormattedCustomerCompany {
  org: string;
  company: string;
  orgId: string;
  salesPermit?: RiskLevel;
}

export interface LanguageSkill {
  id: string;
  languageCode: LanguageSkillCode;
  skillLevel: string;
}

export type LanguageSkillOption = Omit<LanguageSkill, "id">;

export interface OrderRowDescriptions {
  description: string;
  descriptionForTalent: string;
  descriptionEn: string;
  descriptionForTalentEn: string;
}

export const formattedCustomerCompanyDefaultValue = {
  org: "",
  company: "",
  orgId: "",
  salesPermit: undefined,
};
export const longOrderDefaultValues = {
  id: "",
  workLocation: {
    ...autoCompleteDefaultValue,
    customerOrgId: "",
    contractId: "",
    address: {
      street: "",
      zip: "",
      city: "",
      country: "",
    },
    customerCCs: [],
    info: {},
    descriptionForTalent: "",
    descriptionForTalentEng: "",
    version: 0,
    type: "",
    active: true,
    talentCustomerContactPersons: [],
  },
  workTitleName: "",
  workTitleId: "",
  tes: autoCompleteDefaultValue,
  tesVariation: [],
  salaryLimit: null,
  skills: [],
  certificates: [],
  languages: [],
  trainings: [],
  amount: 1,
  date: new Date(),
  time: "",
  customerCostCenters: [],
  reasonForTemporaryEmployment: "",
  descriptionToEezyAndCustomer: "",
  fixedTermDetails: {
    startDate: new Date(),
    endDate: new Date(),
    shortWorkingHoursFi: "",
    longWorkingHoursFi: "",
    shortWorkingHoursEn: "",
    longWorkingHoursEn: "",
  },
  automatching: {
    talents: [],
    automatchingStatus: AutomatchingStatus.On,
    automatchingStartDelayMinutes: null,
  },
};

export const shortOrderDefaultFieldValues: OrderFields = {
  id: "",
  createdAt: 0,
  workLocation: {
    ...autoCompleteDefaultValue,
    customerOrgId: "",
    contractId: "",
    address: {
      street: "",
      zip: "",
      city: "",
      country: "",
    },
    customerCCs: [],
    info: {},
    descriptionForTalent: "",
    descriptionForTalentEng: "",
    version: 0,
    type: "",
    talentCustomerContactPersons: [],
    active: true,
    invoiceContactPerson: null, // TODO: do we need this?
    invoiceReferences: null, // TODO: do we need this?
  },
  orderTemplate: {
    id: "",
    name: "",
    tesId: "",
    contractId: "",
    tes: autoCompleteDefaultValue,
    salaryLimit: null,
    salaryLimits: [],
    skills: [],
    certificates: [],
    languages: [],
    tesVariation: [],
    trainings: [],
    workTitleId: "",
    supplements: {},
    supplementPriceOverrides: {},
    periodStartDate: null,
    isBillable: true,
  },
  amount: 1,
  date: new Date(),
  time: "",
  customerCostCenter: autoCompleteDefaultValue,
  reasonForTemporaryEmployment: "",
  descriptionToEezyAndCustomer: "",
  descriptionToEezyAndCustomerAndTalent: "",
  isFillShift: false,
  automatching: {
    talents: [],
    automatchingStatus: AutomatchingStatus.On,
    automatchingStartDelayMinutes: null,
    talentsFromWorkLocationPool: false,
    pool: null,
  },
  flexibleStartUntil: "",
  notifyWhenFulfilled: false,
  notifyMinutesBeforeIfNotFulfilled: 0,
};

export const emptyOrderEdit: OrderEdit = {
  bundleId: "",
  title: "",
  type: OrderType.Short,
  orderQueueId: "",
  contractId: "",
  customerOrgId: "",
  customerOrgName: "",
  dateOrdered: "0",
  orderer: "",
  workLocationId: "",
  workLocationName: "",
  amount: 0,
  workStarts: 0,
  workEnds: 0,
  orderRowTemplateId: "",
  orderRowTemplateName: "",
  reasonForTemporaryEmployment: "",
  version: 0,
  status: {
    automatchingStatus: AutomatchingStatus.Off,
    automatchingStartDelayMinutes: null,
    fulfillmentStatus: FulfillmentStatus.None,
    isAutomatchingExhausted: false,
    isReadOnly: true,
    mainStatus: "",
  },
  orderHandler: null,
  orderHandlers: [],
  shiftDetails: [
    {
      id: "",
      orderRowId: "",
      assignedTalent: {
        id: "",
        name: "",
        assignmentId: "",
        talentGetsPaid: false,
      },
      shiftDetail: {
        custCCId: "",
        time: {
          start: 0,
          end: 0,
        },
        isFillShift: false,
        salaryLimit: "",
        isBillable: true,
      },
      version: 0,
    },
  ],
  taskSpec: {
    workTitleId: "",
    languageSkills: [],
    certificateIds: [],
    skillIds: [],
    trainings: [],
    tesId: "",
    tesVariation: [],
  },
  customerCostCenterIds: [],
  // To be deprecated
  orderRows: [
    {
      id: "",
      version: 0,
      shiftDetail: {
        custCCId: "",
        time: {
          start: 0,
          end: 0,
        },
        salaryLimit: "",
        isFillShift: false,
        isBillable: true,
      },
      bundleId: "",
      taskSpec: {
        workTitleId: "",
        skillIds: [""],
        certificateIds: [""],
        trainings: [""],
        languageSkills: [
          { languageCode: "en" as LanguageSkillCode, skillLevel: "" },
        ],
        tesId: "",
        description: "",
        descriptionEn: "",
        descriptionForTalent: "",
        descriptionForTalentEn: "",
        tesVariation: [["", ""]] as VariationSelection[],
        supplements: { "": "" },
        supplementPriceOverrides: {},
        periodStartDate: null,
      },
      descriptionToEezyAndCustomer: "",
      descriptionToEezyAndCustomerAndTalent: "",
      assignedTalents: [],
    },
  ],
  eezyCompanyId: "",
  targetedShiftTargetingRules: [],
};

type OrderPreparedTime =
  | {
      end: EpochTimeStamp;
      start: EpochTimeStamp;
      startRange?: never; // disallow startRange if start is present
    }
  | {
      end: EpochTimeStamp;
      startRange: {
        start: EpochTimeStamp;
        end: EpochTimeStamp;
      };
      start?: never; // disallow start if startRange is present
    };

export interface TeamNotificationSettings {
  notifyWhenFulfilled?: boolean;
  notifyMinutesBeforeIfNotFulfilled?: number;
}

export type OrderPreparedPayloadRow = {
  bundleId: string;
  customerOrgId: string;
  customerCostCenterId: string;
  workLocationId: string;
  workTitleId: string;
  reasonForTemporaryEmployment: string;
  tesVariation: VariationSelection[];
  time: OrderPreparedTime;
  supplementPriceOverrides: SupplementPriceOverridesType;
  description: string;
  descriptionEn: string;
  descriptionForTalent: string;
  descriptionForTalentEn: string;
  tesId: string;
  salaryLimit: string | null;
  isFillShift: boolean;
  talentIdsToOfferManually: string[];
  automatchingStatus: AutomatchingStatus;
  automatchingStartDelayMinutes: number | null;
  orderRowTemplateId: string; //OrderRowTemplate which was used to create this orderRow
  periodStartDate: string | null;
  teamNotificationSettings?: TeamNotificationSettings;
  isBillable: boolean;
};
//TODO: use same type with the components
export type LongOrderPreparedPayloadRow = {
  requiredTalentAmount: number;
  contractId: string;
  customerOrgId: string;
  customerCostCenterIds: string[];
  workLocationId: string;
  workTitleId: string;
  tesId: string;
  tesVariation: VariationSelection[];
  reasonForTemporaryEmployment: string;
  startDate: number;
  endDate: number;
  orderRowTemplateId: string;
  periodStartDate?: string;
  shortWorkTimeDescriptionFi: string;
  shortWorkTimeDescriptionEn: string;
  longWorkTimeDescriptionFi: string;
  longWorkTimeDescriptionEn: string;
  workDescriptionFi: string;
  workDescriptionEn: string;
  arrivalInstructionsFi: string;
  arrivalInstructionsEn: string;
  descriptionToEezyAndCustomer: string;
  supplements: SupplementsType;
  languages: LanguageSkill[];
  certificates: string[];
  skills: string[];
  trainings: string[];
  supplementPriceOverrides: SupplementPriceOverridesType; // supplement key: price
  isBillable: boolean;
  targetedShiftTargetingRules: TargetingRule[];
};

export type OrderPreparedPayload = {
  title: string;
  orderRows: OrderPreparedPayloadRow[] | LongOrderPreparedPayloadRow[];
};

export type OrderRowEditedPayload = {
  orderRow: OrderRowEdit;
  reasonForEditing: string;
};

export type Bundling = {
  show: boolean;
  bundleId: string | null;
  bundleTemplate: string | null;
  bundleWorkLocation: string | null;
  bundleReasonForTemporaryEmployment: string | null;
  bundleAmount: number | null;
  resetManuallyAddedTalents: boolean;
  resetTimeBasedNotification: boolean;
};

export const bundlingDefaultValues = {
  show: false,
  bundleId: null,
  bundleTemplate: null,
  bundleWorkLocation: null,
  bundleReasonForTemporaryEmployment: null,
  bundleAmount: null,
  resetManuallyAddedTalents: false,
  resetTimeBasedNotification: false,
};

export type OrderAdditionalInfo = {
  show: boolean;
  rowId: string;
  id: string;
};

export type ValidationError = {
  id: string | null;
  field: string | null;
  errorType: string;
};

export type AutocompleteOptions = {
  workLocations: AutocompleteType[];
  costCenters: CustomerCC[];
};

export type FulfillmentStatusResponse = {
  fulfilled: boolean;
} | null;
